import React from 'react';
import { Box, Typography, Button, IconButton, Badge, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles'; // IMPORT THÊM HÀM ALPHA
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import SecureImage from './SecureImage'; 

// ── Giá trị cũ của field File không phải lúc nào cũng là id file ─────────────────────────────
// Nhiều bản ghi cũ (vd rpt.file_mau_in) lưu ĐƯỜNG DẪN chứ không lưu id:
//   - '/api/<id_app>/file/download/<id>'  (UI cũ upload qua /file)
//   - '/templates/excels/<...>.xlsx'       (UI cũ upload qua /uploadexcel, hoặc "Mẫu chuẩn" tự tạo)
//   - './templates/excels/hd1.xlsx'        (dữ liệu mẫu hệ thống)
//   - 'http(s)://...'                      (link tuyệt đối)
// Trước đây control ghép thẳng '/file/download/' + giá trị -> ra link hỏng kiểu
// '/api/x/file/download//templates/excels/a.xlsx'. Hai hàm dưới đọc được mọi dạng trên.
const RE_FILE_DOWNLOAD = /\/file\/download\/([^/?#]+)/;

/** Lấy id file từ id / object file / đường dẫn '/file/download/<id>'. Không phải file trong kho -> ''. */
export const layIdFileTuGiaTri = (giaTri) => {
  if (!giaTri) return '';
  if (typeof giaTri === 'object') return String(giaTri._id || giaTri.id || '');
  const s = String(giaTri).trim();
  const m = s.match(RE_FILE_DOWNLOAD);
  if (m) return m[1];
  return s.indexOf('/') >= 0 ? '' : s;
};

/** Dựng link tải cho một giá trị của field File. `extraQuery` vd '&size=XS'. */
export const taoUrlTaiFile = (giaTri, context, userToken, extraQuery = '') => {
  if (!giaTri) return null;
  const serverUrl = context?.config?.server_url || '';
  const idApp = context?.config?.id_app || context?.appInfo?._id || '';
  const token = `access_token=${userToken || ''}`;

  const id = layIdFileTuGiaTri(giaTri);
  if (id) return `${serverUrl}/api/${idApp}/file/download/${id}?${token}${extraQuery}`;

  const raw = typeof giaTri === 'object' ? (giaTri.download_link || giaTri.url || '') : String(giaTri).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) {
    // Link ngoài giữ nguyên; link về chính máy chủ thì gắn token để qua được lớp xác thực.
    if (serverUrl && raw.indexOf(serverUrl) === 0) return `${raw}${raw.indexOf('?') >= 0 ? '&' : '?'}${token}${extraQuery}`;
    return raw;
  }
  // Đường dẫn tương đối trên máy chủ (vd /templates/...): chuẩn hoá './x' | 'x' -> '/x'
  const duongDan = '/' + raw.replace(/^\.?\/+/, '');
  return `${serverUrl}${duongDan}${duongDan.indexOf('?') >= 0 ? '&' : '?'}${token}${extraQuery}`;
};

/** Tên hiển thị khi giá trị chỉ là đường dẫn (chưa có bản ghi file). */
const tenTuDuongDan = (giaTri) => {
  if (typeof giaTri !== 'string' || giaTri.indexOf('/') < 0 || RE_FILE_DOWNLOAD.test(giaTri)) return '';
  const cuoi = giaTri.split(/[?#]/)[0].split('/').pop() || '';
  try { return decodeURIComponent(cuoi); } catch (e) { return cuoi; }
};

export default function FileFieldControl({ 
  field, value, onChange, error, isReadOnly, context, userToken, isUploading, saving 
}) {
  const isMultiple = field.type === 'FileArray';
  const isAvatar = field.isAvatar === true; 
  const acceptTypes = isAvatar ? "image/*" : (field.accept || "*/*"); 
  const fileList = isMultiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const helperTextStr = error || field.helpText || '';

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files || files.length === 0) return;
    if (isMultiple) onChange([...fileList, ...files]);
    else onChange(files[0]); 
    event.target.value = null; 
  };

  const handleRemoveFile = (indexToRemove) => {
    if (isMultiple) {
      const newFiles = fileList.filter((_, idx) => idx !== indexToRemove);
      onChange(newFiles);
    } else {
      onChange(null);
    }
  };

  const handleToggleAvatar = (index) => {
    const newFiles = [...fileList];
    const file = newFiles[index];
    if (file instanceof File) file.is_avatar = !file.is_avatar;
    else newFiles[index] = { ...file, is_avatar: !file.is_avatar };
    onChange(newFiles);
  };

  const handleSecureDownload = async (e, url, fileName) => {
    e.preventDefault(); e.stopPropagation();
    try {
      context.setProgressStatus && context.setProgressStatus(true);
      context.apis.downloadFile(url, fileName, (e) => {
        if(e) return context.alert(e.message||e.error||e, null, "red");
        context.setProgressStatus && context.setProgressStatus(false);
      });
    } catch (err) { context.alert("Tải file thất bại", null, "red"); } 
    finally { context.setProgressStatus && context.setProgressStatus(false); }
  };

  // ================= RENDER CHẾ ĐỘ AVATAR HÌNH TRÒN =================
  if (isAvatar) {
    if (isMultiple) {
      return (
        <Box sx={{ p: 1.5, bgcolor: isReadOnly ? 'action.hover' : 'transparent', borderRadius: '12px', border: '1px solid', borderColor: error ? 'error.main' : 'transparent' }}>
          {field.label && (
            <Typography variant="body2" fontWeight="600" sx={{ mb: 1.5, color: error ? 'error.main' : 'text.primary' }}>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {fileList.map((fileObj, idx) => {
              const isRawFile = fileObj instanceof File;
              const fileId = isRawFile ? `raw_${idx}` : (fileObj?._id || fileObj?.id || fileObj);
              const avatarUrl = isRawFile ? URL.createObjectURL(fileObj) : taoUrlTaiFile(fileObj, context, userToken, '&size=M');

              return (
                <Badge
                  key={fileId}
                  overlap="circular"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  badgeContent={
                    !isReadOnly && (
                      <IconButton
                        size="small"
                        disabled={isUploading || saving}
                        onClick={() => handleRemoveFile(idx)}
                        // NÚT NỔI TRÊN ẢNH -> Dùng màu ĐẶC (Solid)
                        sx={{ bgcolor: 'error.main', color: 'white', width: 22, height: 22, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'error.dark' } }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )
                  }
                >
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: '2px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isRawFile ? <img src={avatarUrl} alt={`Avatar ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <SecureImage src={avatarUrl} alt={`Avatar ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </Box>
                </Badge>
              );
            })}

            {!isReadOnly && (
              <Box
                component="label"
                sx={{
                  width: 72, height: 72, borderRadius: '50%', border: '2px dashed', borderColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  bgcolor: 'action.hover', opacity: (isUploading || saving) ? 0.5 : 1, pointerEvents: (isUploading || saving) ? 'none' : 'auto', transition: 'all 0.2s', '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <PhotoCameraIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <input type="file" hidden multiple accept={acceptTypes} onChange={handleFileSelect} />
              </Box>
            )}
          </Box>
          {helperTextStr && <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>{helperTextStr}</Typography>}
        </Box>
      );
    }

    const singleFile = fileList[0];
    const isRawFile = singleFile instanceof File;
    const fileId = isRawFile ? null : (singleFile?._id || singleFile?.id || singleFile);
    const avatarUrl = singleFile ? (isRawFile ? URL.createObjectURL(singleFile) : taoUrlTaiFile(singleFile, context, userToken, '&size=M')) : null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            !isReadOnly && (
              <IconButton
                component="label"
                disabled={isUploading || saving}
                // NÚT NỔI TRÊN ẢNH -> Dùng màu ĐẶC (Solid)
                sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 36, height: 36, boxShadow: '0 2px 8px rgba(0,0,0,0.2)', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <PhotoCameraIcon sx={{ fontSize: 20 }} />
                <input type="file" hidden accept={acceptTypes} onChange={handleFileSelect} />
              </IconButton>
            )
          }
        >
          <Box sx={{ width: 110, height: 110, borderRadius: '50%', overflow: 'hidden', border: '3px solid', borderColor: error ? 'error.main' : 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            {avatarUrl ? (
              isRawFile ? <img src={avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <SecureImage src={avatarUrl} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.5 }} />
            )}
          </Box>
        </Badge>
        {field.label && <Typography variant="body2" fontWeight="600" sx={{ mt: 2, color: error ? 'error.main' : 'text.primary' }}>{field.label} {field.required && <span style={{ color: 'red' }}>*</span>}</Typography>}
        {helperTextStr && <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5, textAlign: 'center' }}>{helperTextStr}</Typography>}
      </Box>
    );
  }

  // ================= RENDER CHẾ ĐỘ FILE ĐÍNH KÈM THÔNG THƯỜNG =================
  return (
    <Box sx={{ bgcolor: isReadOnly ? 'action.hover' : 'transparent', p: 1.5, borderRadius: '12px', border: '1px solid', borderColor: error ? 'error.main' : 'divider' }}>
      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, color: error ? 'error.main' : 'text.primary' }}>
        {field.label} {field.required && <span style={{color:'red'}}>*</span>}
      </Typography>

      {fileList.length > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {fileList.map((fileObj, idx) => {
            const isRawFile = fileObj instanceof File;
            const fileId = isRawFile ? `raw_${idx}` : (fileObj._id || fileObj.id || fileObj); 
            const fileName = isRawFile ? fileObj.name : (fileObj.file?.originalname || fileObj.file_name || fileObj.name || tenTuDuongDan(fileObj) || `File đính kèm ${idx + 1}`);
            const fileExt = isRawFile ? fileObj.name.split('.').pop().toLowerCase() : (fileObj.file?.extension || fileName.split('.').pop()).toLowerCase();
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
            const isMarkedAsAvatar = fileObj.is_avatar === true;

            const fullDownloadUrl = isRawFile ? null : taoUrlTaiFile(fileObj, context, userToken);
            const thumbnailUrl = isImage ? (isRawFile ? URL.createObjectURL(fileObj) : taoUrlTaiFile(fileObj, context, userToken, '&size=XS')) : null;

            return (
              <Box key={fileId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: isMarkedAsAvatar ? '#ffb300' : 'divider', borderRadius: 2, bgcolor: isRawFile ? 'action.selected' : 'background.paper', transition: 'border-color 0.2s' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  {thumbnailUrl ? (
                    isRawFile ? (
                      <Box component="img" src={thumbnailUrl} alt={fileName} sx={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 1.5, mr: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', flexShrink: 0 }} />
                    ) : (
                      <SecureImage src={thumbnailUrl} alt={fileName} style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, marginRight: 12, border: '1px solid', borderColor: 'divider', backgroundColor: '#f2f2f7', flexShrink: 0 }} />
                    )
                  ) : (
                    <Box sx={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, mr: 1.5, bgcolor: isRawFile ? 'primary.light' : 'background.default', flexShrink: 0 }}>
                      {isRawFile ? <CloudUploadOutlinedIcon color="primary"/> : <AttachFileIcon sx={{ color: 'text.secondary' }}/>}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, flexGrow: 1 }}>
                    {isRawFile ? (
                       <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500, lineHeight: 1.2, mb: 0.5, display: 'block' }}>{fileName}</Typography>
                    ) : (
                       <Typography variant="body2" noWrap sx={{ color: 'primary.main', fontWeight: 500, textDecoration: 'none', lineHeight: 1.2, mb: 0.5, cursor: 'pointer', display: 'block' }} component="a" onClick={(e) => handleSecureDownload(e, fullDownloadUrl, fileName)}>{fileName}</Typography>
                    )}
                    {isRawFile && (
                       <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, lineHeight: 1 }}>
                         <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main' }} />{(fileObj.size / 1024 / 1024).toFixed(2)} MB
                       </Typography>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  
                  {/* NÚT SAO: SOFT UI (Không viền, nền nhạt khi hover hoặc active) */}
                  {isImage && (
                    <Tooltip title={isMarkedAsAvatar ? "Bỏ ảnh đại diện" : "Chọn làm đại diện"}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleToggleAvatar(idx)} 
                        disabled={isReadOnly || isUploading || saving}
                        sx={{ 
                          p: 0.5, mr: 0.5, 
                          color: isMarkedAsAvatar ? '#ffb300' : 'text.disabled',
                          bgcolor: isMarkedAsAvatar ? alpha('#ffb300', 0.1) : 'transparent',
                          '&:hover': { bgcolor: isMarkedAsAvatar ? alpha('#ffb300', 0.2) : 'action.hover' }
                        }}
                      >
                        {isMarkedAsAvatar ? <StarIcon fontSize="small"/> : <StarBorderIcon fontSize="small"/>}
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* NÚT XÓA: SOFT UI (Nền đỏ nhạt, Icon đỏ đậm) */}
                  {!isReadOnly && (
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveFile(idx)} 
                      disabled={isUploading || saving}
                      sx={{ 
                        p: 0.5, 
                        color: 'error.main', 
                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.2) }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small"/>
                    </IconButton>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      {!isReadOnly && (
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={isUploading || saving} sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 'bold', borderColor: 'divider', color: 'text.primary' }}>
          {context.apis?.getLabel ? context.apis.getLabel("Chọn file") : "Chọn file đính kèm"}
          <input type="file" hidden multiple={isMultiple} accept={acceptTypes} onChange={handleFileSelect} />
        </Button>
      )}
      {helperTextStr && <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>{helperTextStr}</Typography>}
    </Box>
  );
}